import React, { type FunctionComponent, useState, useEffect } from 'react'
import { css } from '@emotion/react'
import { Button, SimpleSelect, spacing } from '@retailer-platform/shared-components'
import { Input } from 'gin-and-tonic/common/input/Input'
import FilterBar from 'legacy/components/FilterBar'
import { useDashMessages } from 'intl/intl.hooks'
import { useGoToPath } from 'utils/routing/navigation.hooks'
import {
  type SearchContentHeroBannerSearchQueryVariables,
  type PlacementFilterInput,
} from 'domains/merchandising/api'
import { filterOptions } from 'domains/merchandising/constants/heroBanner.constants'
import { useRetailerId } from 'utils/routing/params.hooks'
import { useSelectOptionsDashMessages } from 'domains/merchandising/utils/intl.hooks'
import { useOnEnterHandler } from 'legacy/common/utils/keyboard/keyboard.utils'

const filterContainer = css({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
})

const filtersLeft = css({
  display: 'flex',
  alignItems: 'center',
})

const searchStyles = css({
  height: 32,
})

const searchWrapper = css({
  margin: `0 ${spacing.X16} 0 0`,
  padding: 0,
  width: 320,
})

const selectWrapper = css({
  marginRight: spacing.X16,
  width: 200,
})

const addButtonStyles = css({
  flex: '0 0 auto',
  margin: 0,
})

const clearButtonStyles = css({
  textTransform: 'capitalize',
  margin: 0,
})

export interface HeroBannersListFiltersProps {
  searchHeroBanners: (variables: SearchContentHeroBannerSearchQueryVariables) => void
}

const HeroBannersListFilters: FunctionComponent<HeroBannersListFiltersProps> = ({
  searchHeroBanners,
}) => {
  const messages = useDashMessages({
    addButton: 'merchandising.hero-banners.new',
    searchPlaceholder: 'merchandising.hero-banners.filters.search.placeholder',
    platformPlaceholder: 'merchandising.hero-banners.filters.platform.placeholder',
    statusPlaceholder: 'merchandising.hero-banners.filters.status.placeholder',
    typePlaceholder: 'merchandising.hero-banners.filters.type.placeholder',
    clear: 'button.clear',
  })
  const { type, platform, status } = useSelectOptionsDashMessages(filterOptions)

  const goToCreateHeroBanner = useGoToPath('merchandising-hero-banners-create')
  const retailerId = useRetailerId()

  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState<PlacementFilterInput>({})

  const updateFilters = newFilters => {
    setFilters({ ...filters, ...newFilters })
  }

  useEffect(() => {
    searchHeroBanners({
      retailerId,
      filter: filters,
    })
  }, [filters, retailerId, searchHeroBanners])

  const onEnterHandler = useOnEnterHandler(() => updateFilters({ searchTerm }))

  return (
    <FilterBar padding="15px 20px">
      <div css={filterContainer}>
        <div css={filtersLeft}>
          <Input
            value={searchTerm}
            onChange={e => setSearchTerm(e.target?.value)}
            onKeyPress={onEnterHandler}
            outerStyles={searchWrapper}
            styles={searchStyles}
            type="text"
            placeholder={messages.searchPlaceholder}
            name="filter-search"
          />
          <div css={selectWrapper}>
            <SimpleSelect
              placeholder={messages.platformPlaceholder}
              value={filters.platform?.[0]}
              onChange={platform => updateFilters({ platform: platform && [platform] })}
              options={platform}
              isClearable
            />
          </div>
          <div css={selectWrapper}>
            <SimpleSelect
              placeholder={messages.typePlaceholder}
              value={filters.type}
              onChange={type => updateFilters({ type })}
              options={type}
              isClearable
            />
          </div>
          <div css={selectWrapper}>
            <SimpleSelect
              placeholder={messages.statusPlaceholder}
              value={filters.status}
              onChange={status => updateFilters({ status })}
              options={status}
              isClearable
            />
          </div>
          <Button
            onClick={() => {
              setSearchTerm('')
              setFilters({})
            }}
            css={clearButtonStyles}
            variant="tertiary"
          >
            {messages.clear}
          </Button>
        </div>

        <Button onClick={goToCreateHeroBanner} css={addButtonStyles}>
          {messages.addButton}
        </Button>
      </div>
    </FilterBar>
  )
}

export default HeroBannersListFilters
